import {
  Complaint,
  ComplaintAttachment,
  ComplaintDefect,
  ComplaintStatus,
  ComplaintStatusRaw,
} from '@/models/Complaint';

const KNOWN_STATUSES: ComplaintStatus[] = [
  'submitted',
  'under_review',
  'approved',
  'resolved',
  'rejected',
  'closed',
];

const STATUS_ALIASES: Record<string, ComplaintStatus> = {
  submitted: 'submitted',
  open: 'submitted',
  pending: 'submitted',
  new: 'submitted',
  filed: 'submitted',

  under_review: 'under_review',
  in_review: 'under_review',
  reviewing: 'under_review',
  review: 'under_review',
  investigating: 'under_review',
  in_progress: 'under_review',

  approved: 'approved',
  accepted: 'approved',
  granted: 'approved',

  resolved: 'resolved',
  fixed: 'resolved',
  completed: 'resolved',

  rejected: 'rejected',
  declined: 'rejected',
  denied: 'rejected',

  closed: 'closed',
  cancelled: 'closed',
  canceled: 'closed',
};

/**
 * Normalize any raw status string to one of the six canonical complaint
 * statuses. Handles unknown/legacy values, dashes, mixed case, and falls back
 * to "submitted" so the pill never renders blank.
 */
export const normalizeComplaintStatus = (raw: ComplaintStatusRaw): ComplaintStatus => {
  if (!raw || typeof raw !== 'string') return 'submitted';
  const key = raw.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!key) return 'submitted';
  if ((KNOWN_STATUSES as string[]).includes(key)) return key as ComplaintStatus;
  return STATUS_ALIASES[key] ?? 'submitted';
};

/**
 * Pull complaints off an order, supporting both the new `complaints[]` shape
 * and the legacy single `complaint` shape. Always returns a newest-first list.
 */
export const getComplaintsArray = (
  order: { complaints?: Complaint[] | null; complaint?: Complaint | null } | null | undefined
): Complaint[] => {
  if (!order) return [];

  let list: Complaint[] = [];
  if (Array.isArray(order.complaints) && order.complaints.length > 0) {
    list = [...order.complaints];
  } else if (order.complaint) {
    list = [order.complaint];
  }

  return list
    .filter((c): c is Complaint => !!c)
    .sort((a, b) => {
      const aTime = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return bTime - aTime;
    });
};

/**
 * True when a status_activities entry represents the order-completed milestone.
 * Handles API casing/spacing variants (e.g. "Completed", "order_completed").
 */
export const isOrderCompletedActivityStatus = (raw: string | null | undefined): boolean => {
  if (raw == null || raw === '') return false;
  const key = String(raw).trim().toLowerCase().replace(/[\s-]+/g, '_');
  return key === 'completed' || key === 'order_completed';
};

type OrderLike = { complaints?: Complaint[] | null; complaint?: Complaint | null };

/**
 * If the detail endpoint returns no complaint payload but the dashboard row's
 * `order` projection still has complaints, copy them onto the detail object so
 * the timeline and header stay in sync.
 */
export const mergeComplaintsOntoOrderDetail = <T extends OrderLike>(
  detail: T | null | undefined,
  fallbackOrder: OrderLike | null | undefined
): T | null => {
  if (detail == null) return null;
  if (getComplaintsArray(detail).length > 0) return detail;
  const fromFallback = getComplaintsArray(fallbackOrder ?? null);
  if (fromFallback.length === 0) return detail;
  return { ...detail, complaints: fromFallback, complaint: null } as T;
};

export const getLatestComplaint = (
  order: { complaints?: Complaint[] | null; complaint?: Complaint | null } | null | undefined
): Complaint | null => {
  const list = getComplaintsArray(order);
  return list.length > 0 ? list[0] : null;
};

export const getExtraComplaintCount = (
  order: { complaints?: Complaint[] | null; complaint?: Complaint | null } | null | undefined
): number => {
  const list = getComplaintsArray(order);
  return list.length > 1 ? list.length - 1 : 0;
};

export interface ComplaintTheme {
  status: ComplaintStatus;
  label: string;
  pillClass: string;
  cardClass: string;
  headingClass: string;
  bodyClass: string;
  subtextClass: string;
  labelMutedClass: string;
  subtext: string;
}

const THEME_BY_STATUS: Record<ComplaintStatus, Omit<ComplaintTheme, 'status'>> = {
  submitted: {
    label: 'Complaint Submitted',
    pillClass: 'bg-amber-100 text-amber-800 border border-amber-200',
    cardClass: 'bg-amber-50 border border-amber-200',
    headingClass: 'text-amber-800',
    bodyClass: 'text-amber-900',
    subtextClass: 'text-amber-700',
    labelMutedClass: 'text-amber-700',
    subtext: 'This complaint is under review. The customer will be contacted soon.',
  },
  under_review: {
    label: 'Complaint Under Review',
    pillClass: 'bg-amber-100 text-amber-800 border border-amber-200',
    cardClass: 'bg-amber-50 border border-amber-200',
    headingClass: 'text-amber-800',
    bodyClass: 'text-amber-900',
    subtextClass: 'text-amber-700',
    labelMutedClass: 'text-amber-700',
    subtext: 'This complaint is under review. The customer will be contacted soon.',
  },
  approved: {
    label: 'Complaint Approved',
    pillClass: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
    cardClass: 'bg-emerald-50 border border-emerald-200',
    headingClass: 'text-emerald-800',
    bodyClass: 'text-emerald-900',
    subtextClass: 'text-emerald-700',
    labelMutedClass: 'text-emerald-700',
    subtext: 'This complaint has been approved. The resolution will follow shortly.',
  },
  resolved: {
    label: 'Complaint Resolved',
    pillClass: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
    cardClass: 'bg-emerald-50 border border-emerald-200',
    headingClass: 'text-emerald-800',
    bodyClass: 'text-emerald-900',
    subtextClass: 'text-emerald-700',
    labelMutedClass: 'text-emerald-700',
    subtext: 'This complaint has been resolved.',
  },
  rejected: {
    label: 'Complaint Rejected',
    pillClass: 'bg-rose-100 text-rose-800 border border-rose-200',
    cardClass: 'bg-rose-50 border border-rose-200',
    headingClass: 'text-rose-800',
    bodyClass: 'text-rose-900',
    subtextClass: 'text-rose-700',
    labelMutedClass: 'text-rose-700',
    subtext: 'This complaint was reviewed and could not be approved.',
  },
  closed: {
    label: 'Complaint Closed',
    pillClass: 'bg-gray-100 text-gray-700 border border-gray-200',
    cardClass: 'bg-gray-50 border border-gray-200',
    headingClass: 'text-gray-800',
    bodyClass: 'text-gray-700',
    subtextClass: 'text-gray-500',
    labelMutedClass: 'text-gray-500',
    subtext: 'This complaint has been closed.',
  },
};

export const getComplaintStatusTheme = (raw: ComplaintStatusRaw): ComplaintTheme => {
  const status = normalizeComplaintStatus(raw);
  return { status, ...THEME_BY_STATUS[status] };
};

const toQuantity = (value: ComplaintDefect['quantity']): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const formatDefectFragment = (defect: ComplaintDefect): string | null => {
  const productName = (defect.product_name ?? '').trim();
  const qty = toQuantity(defect.quantity);
  if (!productName || qty <= 0) return null;
  const unit = qty === 1 ? 'unit' : 'units';
  return `${productName} (${qty} ${unit})`;
};

/**
 * Build the auto-generated body line describing the defects, applying
 * singular/plural rules and skipping invalid entries. Falls back to a
 * generic message when no usable defects are present.
 */
export const buildComplaintBodyLine = (complaint: Complaint | null | undefined): string => {
  const fallback = 'Customer submitted a complaint regarding the product quality.';
  if (!complaint) return fallback;

  const fragments = (complaint.defects ?? [])
    .map(formatDefectFragment)
    .filter((f): f is string => !!f);

  if (fragments.length === 0) return fallback;

  let joined: string;
  if (fragments.length === 1) {
    joined = fragments[0];
  } else if (fragments.length === 2) {
    joined = `${fragments[0]} and ${fragments[1]}`;
  } else {
    const head = fragments.slice(0, -1).join(', ');
    const tail = fragments[fragments.length - 1];
    joined = `${head} and ${tail}`;
  }

  return `Customer submitted a complaint regarding print quality on ${joined}.`;
};

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'bmp'];

export const isImageAttachment = (att: ComplaintAttachment | null | undefined): boolean => {
  if (!att || !att.url) return false;
  if (typeof att.mime_type === 'string' && att.mime_type.toLowerCase().startsWith('image/')) {
    return true;
  }
  const source = (att.file_name || att.url || '').toLowerCase();
  const dotIndex = source.lastIndexOf('.');
  if (dotIndex < 0) return false;
  const ext = source.slice(dotIndex + 1).split(/[?#]/)[0];
  return IMAGE_EXTENSIONS.includes(ext);
};

export const getImageAttachments = (
  complaint: Complaint | null | undefined
): ComplaintAttachment[] => {
  if (!complaint || !Array.isArray(complaint.attachments)) return [];
  return complaint.attachments.filter(isImageAttachment);
};

/**
 * Format the ticket number consistently. Pads numeric values to 4 digits and
 * always prefixes with "#CMP-". Returns null when no usable value is present.
 */
export const formatComplaintTicketNo = (
  ticketNo: Complaint['ticket_no']
): string | null => {
  if (ticketNo === null || ticketNo === undefined) return null;
  const raw = String(ticketNo).trim();
  if (!raw) return null;
  if (/^cmp[-_]/i.test(raw) || raw.startsWith('#')) {
    return raw.startsWith('#') ? raw : `#${raw}`;
  }
  if (/^\d+$/.test(raw)) {
    return `#CMP-${raw.padStart(4, '0')}`;
  }
  return `#${raw}`;
};

export const getComplaintCardDomId = (complaint: Complaint | null | undefined): string => {
  const id = complaint?.id ?? complaint?.ticket_no ?? '';
  return `complaint-card-${id}`;
};
